import { connect } from "react-redux";
import ReactLoading from "react-loading";
import { Flex } from "rebass/styled-components";
import React, { useState, useEffect } from "react";
import { useToaster } from "@hellocontento/maillard";

import {
  FullCheckbox,
  SettingsTitle,
  SettingsHeader,
  SettingsSection,
  FullCheckboxTitle,
  SettingsDescription,
  FullCheckboxContainer,
  FullCheckboxDescription
} from "./styles";
import config from "config";
import { emails } from "constants/settings";
import Switch from "components/common/form/Switch";
import IconButton from "components/common/IconButton";
import { useContentoApi } from "utils/useContentoApi";
import * as accountActions from "state/actions/AccountActions";
import * as notificationStyles from "./styles/notifications";
import SlackIcon from "./assets/slack.icon";
import { callApi } from "utils/ContentoApi";

const WEEKDAYS = [
  { isoWeekday: 1, label: "M", name: "Monday" },
  { isoWeekday: 2, label: "T", name: "Tuesday" },
  { isoWeekday: 3, label: "W", name: "Wednesday" },
  { isoWeekday: 4, label: "T", name: "Thursday" },
  { isoWeekday: 5, label: "F", name: "Friday" },
  { isoWeekday: 6, label: "S", name: "Saturday" },
  { isoWeekday: 7, label: "S", name: "Sunday" }
];

const TaskReminderDays = ({ days, onToggleDay }) => (
  <notificationStyles.DayPicker>
    {WEEKDAYS.map(({ isoWeekday, label, name }) => {
      const selected = days.includes(isoWeekday);
      return (
        <notificationStyles.DayButton
          key={isoWeekday}
          type="button"
          selected={selected}
          aria-label={name}
          aria-pressed={selected}
          // The last chosen day stays on; turning the reminder off is the switch's job.
          disabled={selected && days.length === 1}
          onClick={() => onToggleDay(isoWeekday)}
        >
          {label}
        </notificationStyles.DayButton>
      );
    })}
  </notificationStyles.DayPicker>
);

const NotificationSetting = ({
  onToggle,
  selected,
  emailName,
  emailDescription,
  children
}) => {
  return (
    <FullCheckboxContainer>
      <Flex flexDirection="column">
        <FullCheckboxTitle>{emailName}</FullCheckboxTitle>
        <FullCheckboxDescription mt={10}>
          {emailDescription}
        </FullCheckboxDescription>
        {children}
      </Flex>
      <FullCheckbox>
        <Switch htmlFor={emailName} checked={selected} onToggle={onToggle} />
      </FullCheckbox>
    </FullCheckboxContainer>
  );
};

const Notifications = ({ account, currentUser }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [slackChannel, setSlackChannel] = useState(null);
  const [disabledEmailNotifications, setDisabledEmailNotifications] = useState(
    []
  );
  const [taskNotificationDays, setTaskNotificationDays] = useState([]);
  const [fetchUserAccount, cancelFetchUserAccount] = useContentoApi(
    `users/${currentUser.id}/accounts/${account.id}`
  );
  const [updateUserAccount] = useContentoApi(
    `users/${currentUser.id}/accounts/${account.id}`,
    "put"
  );
  const addToast = useToaster();

  useEffect(() => {
    callApi({
      method: "get",
      url: `/accounts/${account.id}/channels`
    })
      .then(channels => {
        const slackChannels = channels.filter(c => c.service === "slack");
        if (slackChannels.length) {
          setSlackChannel(slackChannels[0]);
        }
        //setLoading(false);
      })
      .catch(() => {
        addToast(
          "There was an error fetching slack channel. Please try again later or contact support.",
          "error"
        );
      });
  }, [account, addToast]);

  useEffect(() => {
    fetchUserAccount()
      .then(userAccount => {
        if (userAccount && userAccount.disabledEmailNotifications) {
          setDisabledEmailNotifications(userAccount.disabledEmailNotifications);
        }
        if (userAccount && userAccount.taskNotificationDays) {
          setTaskNotificationDays(userAccount.taskNotificationDays);
        }
        setIsLoading(false);
      })
      .catch(error => {
        addToast(error.message, "error");
      });

    return cancelFetchUserAccount;
  }, [
    account,
    currentUser,
    fetchUserAccount,
    cancelFetchUserAccount,
    addToast
  ]);

  useEffect(() => {
    callApi({
      method: "get",
      url: `/accounts/${account.id}/channels`
    })
      .then(channels => {
        const slack = channels.filter(c => c.service === "slack");
        if (slack.length && slack.length > 0) {
          setSlackChannel(slack[0]);
        }
      })
      .catch(error => {
        addToast(error.message, "error");
      });
  }, [account, currentUser, addToast]);

  const toggleEmail = emailId => {
    let updatedDisabledEmails = [...disabledEmailNotifications];
    if (updatedDisabledEmails.includes(emailId)) {
      updatedDisabledEmails = updatedDisabledEmails.filter(
        id => id !== emailId
      );
    } else {
      updatedDisabledEmails = [...updatedDisabledEmails, emailId];
    }
    setDisabledEmailNotifications(updatedDisabledEmails);

    updateUserAccount({
      data: {
        disabledEmailNotifications: updatedDisabledEmails
      }
    })
      .then(userAccount => {
        if (userAccount && userAccount.disabledEmailNotifications) {
          setDisabledEmailNotifications(userAccount.disabledEmailNotifications);
        }
      })
      .catch(error => {
        addToast(error.message, "error");
      });
  };

  const toggleTaskReminderDay = isoWeekday => {
    const previousDays = taskNotificationDays;
    const updatedDays = previousDays.includes(isoWeekday)
      ? previousDays.filter(day => day !== isoWeekday)
      : [...previousDays, isoWeekday].sort((a, b) => a - b);
    setTaskNotificationDays(updatedDays);

    updateUserAccount({
      data: {
        taskNotificationDays: updatedDays
      }
    }).catch(error => {
      setTaskNotificationDays(previousDays);
      addToast(error.message, "error");
    });
  };

  /// Using Webcal instead of plain https url breaks syncing of the calenders on our users calender apps
  const webcalLink = `${config.apiUrl}/users/${currentUser.id}/accounts/${account.id}/ics`;

  const copyLink = () => {
    navigator.clipboard.writeText(webcalLink);
    addToast("Copied url to clipboard.", "success");
  };

  const handleSubcribeCalendarClick = () => {
    window.location.href = webcalLink;
    copyLink();
  };

  const handleSlackConnect = () => {
    window.open(
      `${config.apiUrl}/accounts/${account.id}/connect/slack`,
      "_blank"
    );
  };

  const handleDisconnect = () => {
    if (slackChannel) {
      callApi({
        method: "delete",
        url: `/accounts/${account.id}/channels/${slackChannel.id}`
      })
        .then(() => {
          setSlackChannel(null);
        })
        .catch(() =>
          addToast(
            "There was an error deleting the slack channel. Please try again later or contact support.",
            "error"
          )
        );
    }
  };

  // While the automation is off the weekly email never fires, and enabling it reseeds
  // the opt-outs anyway — a visible toggle would accept choices that get discarded.
  const visibleEmails = emails.filter(
    email =>
      email.id !== "scheduled_by_default" ||
      account.contentFactoryAutomationMode === "scheduled_by_default"
  );

  return (
    <>
      <SettingsHeader>
        <SettingsTitle>Notifications</SettingsTitle>
        <SettingsDescription>
          Choose notification preferences and how you want to be contacted.
        </SettingsDescription>
      </SettingsHeader>

      <SettingsSection>
        {isLoading ? (
          <ReactLoading color="#bbb" type="cylon" />
        ) : (
          <>
            {visibleEmails.map(email => (
              <NotificationSetting
                key={email.id}
                emailName={email.name}
                emailDescription={email.description}
                selected={!disabledEmailNotifications.includes(email.id)}
                onToggle={() => toggleEmail(email.id)}
              >
                {email.id === "task_notification" &&
                  !disabledEmailNotifications.includes(email.id) && (
                    <TaskReminderDays
                      days={taskNotificationDays}
                      onToggleDay={toggleTaskReminderDay}
                    />
                  )}
              </NotificationSetting>
            ))}
            <FullCheckboxContainer>
              <Flex flexDirection="column">
                <FullCheckboxTitle>Calendar Integration</FullCheckboxTitle>
                <FullCheckboxDescription className="full-width">
                  Copy this link & import as url into your favorite calendar app
                  to get Willow notifications in your calendar.
                </FullCheckboxDescription>
                <FullCheckboxDescription className="full-width link">
                  {webcalLink}
                </FullCheckboxDescription>
              </Flex>
              <FullCheckbox>
                <IconButton
                  size="sm"
                  iconSize={20}
                  variant="primaryMain"
                  icon="icon-autopilot"
                  onClick={handleSubcribeCalendarClick}
                />
              </FullCheckbox>
            </FullCheckboxContainer>
            {/*<FullCheckboxContainer>
              <Flex flexDirection="column">
                <FullCheckboxTitle>Slack Integration</FullCheckboxTitle>
                <FullCheckboxDescription className="full-width">
                  Receive your notifications in your slack workspace
                </FullCheckboxDescription>
              </Flex>
              {!slackChannel && (
                <FullCheckbox>
                  <AddSlackButton onClick={handleSlackConnect}>
                    <SlackIcon />
                    Add to slack
                  </AddSlackButton>
                </FullCheckbox>
              )}
              {slackChannel && (
                <FullCheckbox>
                  <DisconnectSlackButton onClick={handleDisconnect}>
                    <Flex alignItems="center">
                      <span className="icon-delete"></span>
                      Remove the integration
                    </Flex>
                  </DisconnectSlackButton>
                </FullCheckbox>
              )}
            </FullCheckboxContainer>*/}
          </>
        )}
      </SettingsSection>
    </>
  );
};

const mapStateToProps = state => {
  return {
    account: state.account.data,
    currentUser: state.auth.currentUser
  };
};

export default connect(mapStateToProps, {
  updateAccount: accountActions.updateAccount
})(Notifications);
